import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { lazy, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { getOrderItemPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { TimetableForm } from 'sections/LKSection/shared/PickPointSharedModule/components/TimetableForm/TimetableForm'
import { validateTimetable } from 'sections/LKSection/shared/PickPointSharedModule/components/TimetableForm/validate'
import { Margin, Padding } from 'components/ui'
import { Button, Divider, Flex, Text, TextArea, Title } from 'components/uiKit'
import { getPvzItemRoute } from 'utils/helpers/routes/getRoutes'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { LKRoutesEnum } from 'shared/constants/routes'

import { MIN_UPDATE_REASON_LENGTH, PvzKeys, timetableStatusTranslator } from '../../data'
import { TimetableStatus } from '../../types/types'

const getKey = (key: string) => `${PvzKeys.editTimetablePage}.${key}`

const PvzEditTimetablePage = observer(() => {
  const { viewPvzUiStore: uiStore } = useStore()

  const { pvzId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [loadingUpdate, setLoadingUpdate] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(false)

  const pvz = uiStore.selectedPvz
  const timetable = uiStore.timetable
  const isTimetableStatusWaiting = uiStore.timetableEditStatus === TimetableStatus.waiting
  const disabled = timetable.isNoChanges || uiStore.isSwitchPvzLoading || isTimetableStatusWaiting || loadingStatus

  useAsyncEffect(async () => {
    if (pvzId) {
      setLoadingStatus(true)
      await uiStore.getTimetableEditStatus(+pvzId)
      setLoadingStatus(false)
    }
  }, [pvzId, uiStore])

  useAsyncEffect(async () => {
    if (pvzId) {
      void uiStore.switchPvz(+pvzId)

      if (uiStore.pvzList.length === 0) {
        await uiStore.getPvzList()
      }

      uiStore.setSelectedPvz(pvz ?? uiStore.getPvz(+pvzId))
    }
  }, [pvzId])

  const backButtonHandler = () => {
    if (pvzId) {
      navigate(getPvzItemRoute(+pvzId))
    }
  }

  const updateButtonHandler = async () => {
    if (validateTimetable.validate(timetable, 'boolean', false) && pvz?.id) {
      setLoadingUpdate(true)

      if (await uiStore.updatePvz(pvz.id, ['order_timetable_update'])) {
        navigate(LKRoutesEnum.pvz)
      }

      setLoadingUpdate(false)
    }
  }

  return (
    <ContainerLK>
      <Padding py={32} style={{ width: '100%' }}>
        <Margin mb={32}>
          <Flex gap={16} align="center">
            <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={backButtonHandler} />

            <Title>{t(getKey('Редактирование графика работы'))}</Title>
          </Flex>
        </Margin>

        <Flex vertical style={{ maxWidth: 600 }}>
          {uiStore.timetableEditStatus !== TimetableStatus.none && (
            <Flex vertical>
              <Text strong>{t(getOrderItemPageKey('Статус заявки'))}:</Text>
              <Text>{t(getKey(timetableStatusTranslator[uiStore.timetableEditStatus]))}</Text>
            </Flex>
          )}

          {pvz && (
            <Form layout="vertical" requiredMark={false}>
              <TimetableForm
                uiStore={timetable}
                pickPointType={pvz.typePoint}
                floorLevel={pvz.floorLevel}
                disabled={isTimetableStatusWaiting}
              />

              <Form.Item<{ timetableUpdateReason: string }>
                label={t(getKey('Причина изменения'))}
                rules={[
                  { required: true, message: t(getSharedKey('required')) },
                  { min: 30, message: t(getSharedKey('minLength'), { count: MIN_UPDATE_REASON_LENGTH }) },
                ]}
                name={'timetableUpdateReason'}
              >
                <TextArea
                  value={timetable.timetableUpdateReason}
                  onChange={(e) => timetable.setTimetableUpdateReason(e.target.value)}
                  maxLength={500}
                  placeholder={t(getKey('Укажите причину изменения графика'))}
                  autoSize
                  disabled={isTimetableStatusWaiting}
                />
              </Form.Item>

              <Divider />

              <Button
                htmlType="submit"
                disabled={disabled}
                loading={loadingUpdate || uiStore.isSwitchPvzLoading}
                onClick={updateButtonHandler}
              >
                {t(getKey('Отправить на рассмотрение'))}
              </Button>
            </Form>
          )}
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default PvzEditTimetablePage

export const PvzEditTimetablePageAsync = lazy(() => import('./PvzEditTimetablePage'))
